import { ChevronDown } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cx } from "../util";
import { useFieldControl } from "./Field";

export interface InputProps extends Omit<ComponentProps<"input">, "size" | "prefix"> {
  /** Leading icon inside the input. */
  icon?: ReactNode;
  /** Trailing adornment (unit, button…). */
  suffix?: ReactNode;
  invalid?: boolean;
  size?: "sm" | "md" | "lg";
  /** Class for the outer wrapper. */
  wrapperClassName?: string;
}

export function Input({ icon, suffix, invalid, size = "md", className, wrapperClassName, ...rest }: InputProps) {
  const f = useFieldControl({ ...rest, invalid });
  return (
    <div
      className={cx(
        "gf-input",
        `gf-input--${size}`,
        f.invalid && "gf-input--invalid",
        rest.disabled && "gf-input--disabled",
        !!icon && "gf-input--has-icon",
        wrapperClassName,
      )}
    >
      {icon ? <span className="gf-input__icon">{icon}</span> : null}
      <input
        {...rest}
        id={f.id}
        required={f.required}
        aria-describedby={f["aria-describedby"]}
        aria-invalid={f["aria-invalid"]}
        className={cx("gf-input__control", className)}
      />
      {suffix ? <span className="gf-input__suffix">{suffix}</span> : null}
    </div>
  );
}

export interface TextareaProps extends ComponentProps<"textarea"> {
  invalid?: boolean;
  /** Grow with content up to `maxRows`. */
  autoGrow?: boolean;
  maxRows?: number;
}

export function Textarea({ invalid, autoGrow, maxRows = 12, className, onInput, rows = 3, ...rest }: TextareaProps) {
  const f = useFieldControl({ ...rest, invalid });
  return (
    <textarea
      {...rest}
      rows={rows}
      id={f.id}
      required={f.required}
      aria-describedby={f["aria-describedby"]}
      aria-invalid={f["aria-invalid"]}
      className={cx("gf-textarea", f.invalid && "gf-textarea--invalid", className)}
      onInput={(e) => {
        if (autoGrow) {
          const el = e.currentTarget;
          const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
          el.style.height = "auto";
          el.style.height = `${Math.min(el.scrollHeight + 2, line * maxRows + 24)}px`;
        }
        onInput?.(e);
      }}
    />
  );
}

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentProps<"select">, "size"> {
  options?: ReadonlyArray<SelectOption>;
  /** Adds a first, empty option. */
  placeholder?: string;
  invalid?: boolean;
  size?: "sm" | "md" | "lg";
  wrapperClassName?: string;
}

/** Native <select> (keyboard + screen-reader perfect), brand-styled. */
export function Select({ options, placeholder, invalid, size = "md", className, wrapperClassName, children, ...rest }: SelectProps) {
  const f = useFieldControl({ ...rest, invalid });
  return (
    <div className={cx("gf-select", `gf-select--${size}`, f.invalid && "gf-select--invalid", rest.disabled && "gf-select--disabled", wrapperClassName)}>
      <select
        {...rest}
        id={f.id}
        required={f.required}
        aria-describedby={f["aria-describedby"]}
        aria-invalid={f["aria-invalid"]}
        className={cx("gf-select__control", className)}
      >
        {placeholder !== undefined ? (
          <option value="" disabled={f.required}>
            {placeholder}
          </option>
        ) : null}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {typeof o.label === "string" ? o.label : String(o.value)}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown className="gf-select__chevron" size={16} aria-hidden="true" />
    </div>
  );
}
